import { ErrorMessage, Field, useFormikContext } from "formik";
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { KTIcon } from "../../../../../_metronic/helpers";
import { useAuth } from "../../../../modules/auth";
import { useBlocksByDistrict } from "../../../block/hooks/useBlock";
import {
	useAuditYears,
	useLatestAuditCalendarForSchool,
	useSchoolsByUdiseCode,
	useUdiseCodes,
} from "../../hooks/useSchemeDataEntry";

type Props = {
	isView?: boolean;
	districts: any[];
	schemeId?: number;
	id?: string;
};

const getMinGramSabhaDate = (auditEndDate: string) => {
	if (!auditEndDate) return "";
	const date = new Date(auditEndDate);
	date.setDate(date.getDate() + 1);
	return date.toISOString().split("T")[0];
};

const NMPStep1: React.FC<Props> = ({ isView = false, districts, schemeId, id }) => {
	const { setFieldValue, values } = useFormikContext<any>();
	const { currentUser } = useAuth() as any;

	const { data: udiseCodesData } = useUdiseCodes(
		values.district_id || null,
		schemeId,
	);
	const udiseCodes = udiseCodesData || [];

	// Scoped by district + scheme too — the same UDISE code can be registered
	// under more than one scheme/district in this table (one row per scheme
	// registration, not one row per physical school), so udise_code alone
	// would show duplicate/wrong-scheme entries.
	const { data: schoolsData } = useSchoolsByUdiseCode(
		values.udise_code || null,
		values.district_id || null,
		schemeId,
	);
	const schools = schoolsData || [];

	const { data: auditYears } = useAuditYears();

	// Looked up from the user's own District/UDISE Code/School Name/Gram
	// Sabha Date selection (District/UDISE Code/School Name/Gram Sabha Date
	// stay user-selected — a district can have several audit calendar
	// entries, one per school) to auto-fill Round No/Audit Start-End
	// Date/Audit Year only. Only fetched on add (no id) so it never
	// overrides an existing saved entry being edited/viewed.
	const { data: latestAuditCalendar } = useLatestAuditCalendarForSchool(
		!id ? schemeId || null : null,
		!id ? Number(values.district_id) || null : null,
		!id ? values.udise_code || null : null,
		!id ? Number(values.school_name) || null : null,
		!id ? values.sgs_date || null : null,
	);

	useEffect(() => {
		if (!id && latestAuditCalendar) {
			setFieldValue("round_no", latestAuditCalendar.round_no);
			setFieldValue("audit_start_date", latestAuditCalendar.start_date);
			setFieldValue("audit_end_date", latestAuditCalendar.end_date);
			const matchedYear = auditYears?.find(
				(y: any) => y.name === latestAuditCalendar.financial_year,
			);
			if (matchedYear) {
				setFieldValue("audit_year_id", matchedYear.id);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, latestAuditCalendar, auditYears, setFieldValue]);

	const { data: drpBlocksData } = useBlocksByDistrict(
		values.drp_district_id || 0,
	);
	const drpBlocks = drpBlocksData || [];

	// BRP Details is auto-filled from the logged-in user (no manual employee
	// lookup) — mirrors "Section A: BRP Details" already used for MGNREGS/
	// PMAY-G: Employee Code/Name/Mobile No are read-only, District/Block are
	// pre-filled editable dropdowns.
	useEffect(() => {
		if (!currentUser) return;
		const empCode = currentUser.employee_code || "";
		const name = currentUser.name || "";
		const mobile = currentUser.mobile_no || currentUser.mobile || "";
		const districtId = currentUser.district_id
			? Number(currentUser.district_id)
			: null;

		if (empCode && values.drp_employee_code !== empCode) {
			setFieldValue("drp_employee_code", empCode);
		}
		if (name && values.drp_name !== name) {
			setFieldValue("drp_name", name);
		}
		if (mobile && values.drp_mobile_no !== mobile) {
			setFieldValue("drp_mobile_no", mobile);
		}
		if (districtId && Number(values.drp_district_id) !== districtId) {
			setFieldValue("drp_district_id", districtId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUser]);

	// Sync BRP Block as soon as the block list for the auto-filled district
	// finishes loading (same two-step pattern MGNREGS's SyncFormValues uses).
	useEffect(() => {
		if (!currentUser?.block_id || !drpBlocks.length) return;
		const blockId = Number(currentUser.block_id);
		if (Number(values.drp_block_id) !== blockId) {
			setFieldValue("drp_block_id", blockId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUser, drpBlocks]);

	return (
		<div className="w-100">
			<div className="pb-10 pb-lg-12">
				<h2 className="fw-bolder text-dark">Basic Details</h2>
				<div className="text-muted fw-bold fs-6">
					Provide basic school information for the audit.
				</div>
			</div>

			<div className="row g-9 mb-5">
				<div className="col-md-4 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						District
					</label>
					<Field
						as="select"
						name="district_id"
						className="form-select form-select-solid"
						disabled={isView}
						onChange={(e: any) => {
							setFieldValue("district_id", e.target.value);
							setFieldValue("udise_code", "");
							setFieldValue("school_name", "");
						}}
					>
						<option value="">Select District</option>
						{districts?.map((d: any) => (
							<option key={d.id} value={d.id}>
								{d.name}
							</option>
						))}
					</Field>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="district_id" />
					</div>
				</div>
				<div className="col-md-4 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						UDISE Code
					</label>
					<Field
						as="select"
						name="udise_code"
						className="form-select form-select-solid"
						disabled={isView || !values.district_id}
						onChange={(e: any) => {
							setFieldValue("udise_code", e.target.value);
							setFieldValue("school_name", "");
						}}
					>
						<option value="">Select UDISE Code</option>
						{udiseCodes?.map((u: any) => (
							<option key={u.udise_code || u.id} value={u.udise_code}>
								{u.udise_code}
							</option>
						))}
					</Field>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="udise_code" />
					</div>
				</div>
				<div className="col-md-4 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						School Name
					</label>
					<Field
						as="select"
						name="school_name"
						className="form-select form-select-solid"
						disabled={isView || !values.udise_code}
					>
						<option value="">Select School Name</option>
						{schools?.map((s: any) => (
							<option key={s.id} value={s.id}>
								{s.name}
							</option>
						))}
					</Field>
				</div>
				<div className="col-md-4 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Gram Sabha Date
					</label>
					<Field
						name="sgs_date"
						type="date"
						min={getMinGramSabhaDate(values.audit_end_date)}
						className="form-control form-control-solid"
						disabled={isView}
					/>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="sgs_date" />
					</div>
				</div>
			</div>

			{/* Round No & Dates */}
			<div className="row g-9 mb-5">
				<div className="col-md-3 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Round No
					</label>
					<Field
						name="round_no"
						type="text"
						className="form-control form-control-solid"
						disabled={true}
					/>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="round_no" />
					</div>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Audit Start Date
					</label>
					<Field
						name="audit_start_date"
						type="date"
						className="form-control form-control-solid"
						disabled={true}
					/>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="audit_start_date" />
					</div>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Audit End Date
					</label>
					<Field
						name="audit_end_date"
						type="date"
						min={values.audit_start_date}
						className="form-control form-control-solid"
						disabled={true}
					/>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="audit_end_date" />
					</div>
				</div>
			</div>

			<div className="row g-9 mb-5">
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Audit Year
					</label>
					<Field
						as="select"
						name="audit_year_id"
						className="form-select form-select-solid"
						disabled={true}
					>
						<option value="">Select Audit Year</option>
						{auditYears?.map((year: any) => (
							<option key={year.id} value={year.id}>
								{year.name}
							</option>
						))}
					</Field>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="audit_year_id" />
					</div>
				</div>
			</div>

			{/* BRP & VRP DETAILS */}
			<div className="card shadow-none border p-5 mb-8 mt-6 rounded bg-light-subtle">
				<h3 className="card-title fw-bold fs-3 mb-5 text-dark">
					BRP & VRP DETAILS
				</h3>

				{/* BRP Details */}
				<h5 className="mb-4 text-dark fw-bold">BRP Details</h5>
				<div className="row g-9 mb-8">
					<div className="col-md-3 fv-row">
						<label className="form-label fs-5 fw-bold text-dark">
							Employee Code
						</label>
						<Field
							name="drp_employee_code"
							className="form-control form-control-solid"
							disabled={true}
							readOnly={true}
						/>
					</div>
					<div className="col-md-3 fv-row">
						<label className="form-label fs-5 fw-bold text-dark">
							Name
						</label>
						<Field
							name="drp_name"
							className="form-control form-control-solid"
							disabled={true}
							readOnly={true}
						/>
					</div>
					<div className="col-md-3 fv-row">
						<label className="form-label fs-5 fw-bold text-dark">
							Mobile No
						</label>
						<Field
							name="drp_mobile_no"
							className="form-control form-control-solid"
							disabled={true}
							readOnly={true}
						/>
					</div>
					<div className="col-md-3 fv-row">
						<label className="form-label fs-5 fw-bold text-dark">
							District
						</label>
						<Field
							as="select"
							name="drp_district_id"
							className="form-select form-select-solid"
							disabled={true}
							value={values.drp_district_id ? String(values.drp_district_id) : ""}
							onChange={(e: any) => {
								const val = e.target.value ? Number(e.target.value) : null;
								setFieldValue("drp_district_id", val);
								setFieldValue("drp_block_id", null);
							}}
						>
							<option value="">Select District</option>
							{districts?.map((d: any) => (
								<option key={d.id} value={String(d.id)}>
									{d.name}
								</option>
							))}
						</Field>
					</div>
					<div className="col-md-3 fv-row">
						<label className="form-label fs-5 fw-bold text-dark">
							Block
						</label>
						<Field
							as="select"
							name="drp_block_id"
							className="form-select form-select-solid"
							disabled={true}
							value={values.drp_block_id ? String(values.drp_block_id) : ""}
							onChange={(e: any) => {
								setFieldValue(
									"drp_block_id",
									e.target.value ? Number(e.target.value) : null,
								);
							}}
						>
							<option value="">Select Block</option>
							{drpBlocks?.map((b: any) => (
								<option key={b.id} value={String(b.id)}>
									{b.name}
								</option>
							))}
						</Field>
					</div>
				</div>

				<div className="separator separator-dashed my-6"></div>

				{/* VRP Details (Max 5) */}
				<div className="d-flex justify-content-between align-items-center mb-6">
					<h5 className="mb-0 text-dark fw-bold">VRP Details</h5>
					{!isView && (values.vrp_details?.length || 0) < 5 && (
						<button
							type="button"
							className="btn btn-sm btn-dark"
							onClick={() => {
								const currentList = values.vrp_details || [];
								if (currentList.length >= 5) {
									toast.error("Maximum 5 VRPs allowed");
									return;
								}
								setFieldValue("vrp_details", [
									...currentList,
									{ mobile_no: "", name: "", village_id: "" },
								]);
							}}
						>
							<KTIcon iconName="plus" className="fs-3" /> ADD VRP
						</button>
					)}
				</div>

				{(values.vrp_details || [{ mobile_no: "", name: "", village_id: "" }]).map(
					(vrp: any, index: number) => (
						<div
							key={index}
							className="border p-4 rounded bg-white mb-4 shadow-sm"
						>
							<div className="row g-9 align-items-end">
								<div className="col-md-4 fv-row">
									<label className="form-label fs-7 fw-bold text-dark">
										VRP Contact No.
									</label>
									<Field
										name={`vrp_details.${index}.mobile_no`}
										className="form-control form-control-solid"
										placeholder="Enter contact number"
										disabled={isView}
									/>
								</div>
								<div className="col-md-4 fv-row">
									<label className="form-label fs-7 fw-bold text-dark">
										VRP Name
									</label>
									<Field
										name={`vrp_details.${index}.name`}
										className="form-control form-control-solid"
										placeholder="Enter VRP name"
										disabled={isView}
									/>
								</div>
								<div
									className={
										!isView && index > 0
											? "col-md-3 fv-row"
											: "col-md-4 fv-row"
									}
								>
									<label className="form-label fs-7 fw-bold text-dark">
										VRP Village/Panchayat
									</label>
									<Field
										name={`vrp_details.${index}.village_id`}
										className="form-control form-control-solid"
										placeholder="Enter village/panchayat"
										disabled={isView}
									/>
								</div>
								{!isView && index > 0 && (
									<div className="col-md-1 fv-row text-end">
										<button
											type="button"
											className="btn btn-icon btn-sm btn-light-danger"
											onClick={() => {
												const list = [...(values.vrp_details || [])];
												list.splice(index, 1);
												setFieldValue("vrp_details", list);
											}}
										>
											<KTIcon iconName="trash" className="fs-3" />
										</button>
									</div>
								)}
							</div>
						</div>
					),
				)}
			</div>

			{/* Food Quality - Below Section C: BRP & VRP DETAILS */}
			<div className="row g-9 mb-10">
				<div className="col-md-12 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Overall Quality of Food as per Children's Opinion
					</label>
					<div className="d-flex align-items-center mt-3">
						<label className="form-check form-check-inline form-check-solid me-5">
							<Field
								className="form-check-input"
								name="overall_quality_food"
								type="radio"
								value="Good"
								disabled={isView}
							/>
							<span className="fw-bold ps-2 fs-6">Good</span>
						</label>
						<label className="form-check form-check-inline form-check-solid me-5">
							<Field
								className="form-check-input"
								name="overall_quality_food"
								type="radio"
								value="Satisfactory"
								disabled={isView}
							/>
							<span className="fw-bold ps-2 fs-6">Satisfactory</span>
						</label>
						<label className="form-check form-check-inline form-check-solid">
							<Field
								className="form-check-input"
								name="overall_quality_food"
								type="radio"
								value="Not Good"
								disabled={isView}
							/>
							<span className="fw-bold ps-2 fs-6">Not Good</span>
						</label>
					</div>
				</div>
			</div>

			{/* Averages */}
			<div className="pb-5 pb-lg-6 mt-10">
				<h3 className="fw-bolder text-dark">Average for the Current Year</h3>
			</div>
			<div className="row g-9 mb-5">
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Number of Children for whom Food was Prepared
					</label>
					<Field
						type="number"
						name="avg_current_year_children_prepared"
						className="form-control form-control-solid"
						disabled={isView}
						min="0"
						onKeyDown={(e: any) => {
							if (["-", "e", "E", "+", "."].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onInput={(e: any) => {
							e.target.value = e.target.value.replace(/[^0-9]/g, "");
						}}
					/>
				</div>
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Number of Children who Consumed Food
					</label>
					<Field
						type="number"
						name="avg_current_year_children_consumed"
						className="form-control form-control-solid"
						disabled={isView}
						min="0"
						onKeyDown={(e: any) => {
							if (["-", "e", "E", "+", "."].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onInput={(e: any) => {
							e.target.value = e.target.value.replace(/[^0-9]/g, "");
						}}
					/>
				</div>
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Number of Eggs Consumed by Children
					</label>
					<Field
						type="number"
						name="avg_current_year_eggs_consumed"
						className="form-control form-control-solid"
						disabled={isView}
						min="0"
						onKeyDown={(e: any) => {
							if (["-", "e", "E", "+", "."].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onInput={(e: any) => {
							e.target.value = e.target.value.replace(/[^0-9]/g, "");
						}}
					/>
				</div>
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Quantity of Rice Cooked
					</label>
					<Field
						type="number"
						name="avg_current_year_rice_cooked"
						className="form-control form-control-solid"
						disabled={isView}
						min="0"
						step="0.01"
						onKeyDown={(e: any) => {
							if (["-", "e", "E", "+"].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onInput={(e: any) => {
							let val = e.target.value.replace(/[^0-9.]/g, "");
							const parts = val.split(".");
							if (parts.length > 2) {
								val = `${parts[0]}.${parts.slice(1).join("")}`;
							}
							e.target.value = val;
						}}
					/>
				</div>
			</div>

			<div className="pb-5 pb-lg-6 mt-10">
				<h3 className="fw-bolder text-dark">Average During the Audit Period</h3>
			</div>
			<div className="row g-9 mb-10">
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Number of Children for whom Food was Prepared
					</label>
					<Field
						type="number"
						name="avg_audit_period_children_prepared"
						className="form-control form-control-solid"
						disabled={isView}
						min="0"
						onKeyDown={(e: any) => {
							if (["-", "e", "E", "+", "."].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onInput={(e: any) => {
							e.target.value = e.target.value.replace(/[^0-9]/g, "");
						}}
					/>
				</div>
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Number of Children who Consumed Food
					</label>
					<Field
						type="number"
						name="avg_audit_period_children_consumed"
						className="form-control form-control-solid"
						disabled={isView}
						min="0"
						onKeyDown={(e: any) => {
							if (["-", "e", "E", "+", "."].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onInput={(e: any) => {
							e.target.value = e.target.value.replace(/[^0-9]/g, "");
						}}
					/>
				</div>
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Number of Eggs Consumed by Children
					</label>
					<Field
						type="number"
						name="avg_audit_period_eggs_consumed"
						className="form-control form-control-solid"
						disabled={isView}
						min="0"
						onKeyDown={(e: any) => {
							if (["-", "e", "E", "+", "."].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onInput={(e: any) => {
							e.target.value = e.target.value.replace(/[^0-9]/g, "");
						}}
					/>
				</div>
				<div className="col-md-6 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Quantity of Rice Cooked
					</label>
					<Field
						type="number"
						name="avg_audit_period_rice_cooked"
						className="form-control form-control-solid"
						disabled={isView}
						min="0"
						step="0.01"
						onKeyDown={(e: any) => {
							if (["-", "e", "E", "+"].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onInput={(e: any) => {
							let val = e.target.value.replace(/[^0-9.]/g, "");
							const parts = val.split(".");
							if (parts.length > 2) {
								val = `${parts[0]}.${parts.slice(1).join("")}`;
							}
							e.target.value = val;
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default NMPStep1;
